/**
 * Announcer (B10) — the app's two live regions (§H.3), fed by bus "announce".
 *
 *   <Announcer/>                     regions + the §H.3 moments (useAnnouncements)
 *   <Announcer skip={["search"]}/>   the same, minus moments a module speaks itself
 *   <Announcer auto={false}/>        regions only
 *
 * Visually hidden, always in the DOM (live regions must exist before they speak).
 * One instance owns the regions; extra instances render nothing, so mounting it
 * twice (AppLayout mounts one) never doubles a message. Messages go through
 * AnnounceQueue: duplicates of a moment within 1.5 s are dropped, polite messages
 * within 150 ms are spoken together, assertive ones at once. Nothing is spoken
 * during kiosk attract. A region is cleared 7 s after it spoke.
 */
import { useEffect, useRef, useState } from "react";
import { on } from "@/store/bus";
import { getApp } from "@/store/store";
import { AnnounceQueue, type Politeness } from "./announceQueue";
import { useAnnouncements, type AnnounceMoment } from "./useAnnouncements";
import "./a11y.css";

let owner: symbol | null = null;
const waiting = new Set<() => void>();

function claim(me: symbol): boolean {
  if (owner && owner !== me) return false;
  owner = me;
  return true;
}
function release(me: symbol): void {
  if (owner !== me) return;
  owner = null;
  waiting.forEach((w) => w());
}

const CLEAR_MS = 7000;
/** Gap between emptying a region and filling it, so the same text is spoken again. */
const REFILL_MS = 60;

/*
 * Messages sent before any regions exist. The loader can fail during the very first commit (no
 * WebGL 2 is known at boot), before the owner has claimed and mounted the regions, and that
 * assertive message would otherwise be lost (L4-16). They are kept here and spoken by the first
 * regions to mount, a moment after mounting so the regions are in the accessibility tree first.
 */
const EARLY_KEEP_MS = 15_000;
const EARLY_SPEAK_MS = 250;
const EARLY_MAX = 6;
let regionsLive = false;
const early: Array<{ text: string; politeness: Politeness; at: number }> = [];
on("announce", ({ text, politeness }) => {
  if (regionsLive) return;
  early.push({ text, politeness: politeness ?? "polite", at: performance.now() });
  if (early.length > EARLY_MAX) early.shift();
});

/** Takes the early messages still worth speaking (exported for tests). */
export function takeEarly(now: number = performance.now()): Array<{ text: string; politeness: Politeness }> {
  return early
    .splice(0)
    .filter((m) => now - m.at <= EARLY_KEEP_MS)
    .map(({ text, politeness }) => ({ text, politeness }));
}

function Regions() {
  const politeRef = useRef<HTMLDivElement>(null);
  const assertiveRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const q = new AnnounceQueue();
    const timers = new Set<number>();
    const clearTimers: Partial<Record<Politeness, number>> = {};
    let flushTimer = 0;

    const set = (fn: () => void, ms: number) => {
      const t = window.setTimeout(() => {
        timers.delete(t);
        fn();
      }, ms);
      timers.add(t);
      return t;
    };
    const speak = (p: Politeness, text: string) => {
      const el = p === "assertive" ? assertiveRef.current : politeRef.current;
      if (!el) return;
      el.textContent = "";
      set(() => {
        el.textContent = text;
      }, REFILL_MS);
      if (clearTimers[p]) window.clearTimeout(clearTimers[p]);
      clearTimers[p] = set(() => {
        if (el.textContent === text) el.textContent = "";
      }, CLEAR_MS);
    };
    const flush = () => {
      flushTimer = 0;
      const now = performance.now();
      const { polite, assertive } = q.take(now);
      if (assertive) speak("assertive", assertive);
      if (polite) speak("polite", polite);
      schedule();
    };
    const schedule = () => {
      const due = q.due();
      if (due === null || flushTimer) return;
      flushTimer = set(flush, Math.max(0, due - performance.now()));
    };

    const off = on("announce", ({ text, politeness }) => {
      if (getApp().phase === "attract") return;
      if (q.push({ text, politeness: politeness ?? "polite" }, performance.now())) schedule();
    });
    regionsLive = true;
    // Taken when the timer fires (not now), so a StrictMode unmount/remount keeps them for the second mount.
    set(() => {
      if (getApp().phase === "attract") {
        takeEarly();
        return;
      }
      let pushed = false;
      for (const m of takeEarly()) pushed = q.push(m, performance.now()) || pushed;
      if (pushed) schedule();
    }, EARLY_SPEAK_MS);
    return () => {
      regionsLive = false;
      off();
      timers.forEach((t) => window.clearTimeout(t));
      timers.clear();
      q.clear();
    };
  }, []);

  return (
    <div className="ld-loc-a11y-sr" data-ld-announcer="">
      <div ref={politeRef} role="status" aria-live="polite" aria-atomic="true" />
      <div ref={assertiveRef} role="alert" aria-live="assertive" aria-atomic="true" />
    </div>
  );
}

function AutoAnnouncements({ skip }: { skip?: readonly AnnounceMoment[] }) {
  useAnnouncements({ skip });
  return null;
}

export interface AnnouncerProps {
  /** Speak the §H.3 moments (useAnnouncements). Default true. */
  auto?: boolean;
  /** Moments another module already speaks. */
  skip?: readonly AnnounceMoment[];
}

export function Announcer({ auto = true, skip }: AnnouncerProps = {}) {
  const me = useRef<symbol | null>(null);
  if (me.current === null) me.current = Symbol("announcer");
  const id = me.current;
  const [isOwner, setOwner] = useState(false);

  useEffect(() => {
    const tryClaim = () => setOwner(claim(id));
    tryClaim();
    waiting.add(tryClaim);
    return () => {
      waiting.delete(tryClaim);
      release(id);
      setOwner(false);
    };
  }, [id]);

  if (!isOwner) return null;
  return (
    <>
      <Regions />
      {auto && <AutoAnnouncements skip={skip} />}
    </>
  );
}
